'use client';

import { useState, useEffect } from 'react';
import { useTranslations } from 'next-intl';

export default function ArmyBodyFatCalculator() {
  const t = useTranslations("tools.armyBodyFatCalculator");
  const [screenSize, setScreenSize] = useState('mobile');

  // Calculator state
  const [inputs, setInputs] = useState({
    gender: 'male',
    age: 21,
    weight: 165,
    waist: 32,
    neck: 15,
    hip: 38
  });

  const [results, setResults] = useState<{
    bodyFat: number;
    ageGroup: string;
    maxAllowed: number;
    isPassing: boolean;
  } | null>(null);

  const [errorMessage, setErrorMessage] = useState('');

  // Screen size detection
  useEffect(() => {
    const updateScreenSize = () => {
      const width = window.innerWidth;
      if (width >= 1024) {
        setScreenSize('desktop');
      } else if (width >= 768) {
        setScreenSize('tablet');
      } else {
        setScreenSize('mobile');
      }
    };

    updateScreenSize();
    window.addEventListener('resize', updateScreenSize);
    return () => window.removeEventListener('resize', updateScreenSize);
  }, []);

  // Responsive style helper
  const getResponsiveStyle = (baseStyle: any, mobileOverrides?: any, tabletOverrides?: any, desktopOverrides?: any) => {
    let style = { ...baseStyle };

    if (screenSize === 'mobile' && mobileOverrides) {
      style = { ...style, ...mobileOverrides };
    } else if (screenSize === 'tablet' && tabletOverrides) {
      style = { ...style, ...tabletOverrides };
    } else if (screenSize === 'desktop' && desktopOverrides) {
      style = { ...style, ...desktopOverrides };
    }

    return style;
  };

  // Army body fat standards
  const standards = {
    male: [
      { ageRange: '17-20', min: 17, max: 20, maxBF: 20 },
      { ageRange: '21-27', min: 21, max: 27, maxBF: 22 },
      { ageRange: '28-39', min: 28, max: 39, maxBF: 24 },
      { ageRange: '40+', min: 40, max: 100, maxBF: 26 }
    ],
    female: [
      { ageRange: '17-20', min: 17, max: 20, maxBF: 30 },
      { ageRange: '21-27', min: 21, max: 27, maxBF: 32 },
      { ageRange: '28-39', min: 28, max: 39, maxBF: 34 },
      { ageRange: '40+', min: 40, max: 100, maxBF: 36 }
    ]
  };

  // Calculate body fat percentage
  const calculateBodyFat = (gender: string, weight: number, waist: number): number => {
    let bodyFat: number;

    if (gender === 'male') {
      bodyFat = (86.010 * Math.log10(waist)) - (70.041 * Math.log10(weight)) + 36.76;
    } else {
      bodyFat = (163.205 * Math.log10(waist)) - (97.684 * Math.log10(weight)) - 78.387;
    }

    return Math.max(0, Math.min(100, bodyFat));
  };

  // Get age group standards
  const getAgeGroup = (gender: string, age: number) => {
    const genderStandards = standards[gender as 'male' | 'female'];
    for (let i = 0; i < genderStandards.length; i++) {
      if (age >= genderStandards[i].min && age <= genderStandards[i].max) {
        return genderStandards[i];
      }
    }
    return genderStandards[genderStandards.length - 1];
  };

  // Handle calculation
  const handleCalculate = (e: React.FormEvent) => {
    e.preventDefault();
    setErrorMessage('');

    const { gender, age, weight, waist } = inputs;

    // Validation
    if (age < 17 || age > 100) {
      setErrorMessage(t('errorAge'));
      return;
    }

    if (weight < 50 || weight > 500) {
      setErrorMessage(t('errorWeight'));
      return;
    }

    if (waist < 10 || waist > 100) {
      setErrorMessage(t('errorWaist'));
      return;
    }

    // Calculate
    const bodyFat = calculateBodyFat(gender, weight, waist);
    const ageGroup = getAgeGroup(gender, age);
    const isPassing = bodyFat <= ageGroup.maxBF;

    setResults({
      bodyFat,
      ageGroup: ageGroup.ageRange,
      maxAllowed: ageGroup.maxBF,
      isPassing
    });

    // Scroll to results
    setTimeout(() => {
      document.getElementById('results-section')?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }, 100);
  };

  // Colors
  const colors = {
    primary: '#ff5a70',
    primaryHover: '#f63b3b',
    secondary: '#ffc400',
    accent: '#ff6b95',
    textPrimary: '#2d3748',
    textSecondary: '#4a5568',
    glassBg: 'rgba(255, 255, 255, 0.6)',
    glassBorder: 'rgba(255, 200, 180, 0.2)',
    success: '#10b981',
    successBg: '#dcfce7',
    error: '#ef4444',
    errorBg: '#fee2e2',
  };

  // Styles
  const containerStyle = getResponsiveStyle(
    { maxWidth: '1200px', margin: '0 auto', position: 'relative' as const, zIndex: 1 },
    { padding: '1rem' },
    { padding: '1.5rem' },
    { padding: '2rem' }
  );

  const headerStyle = getResponsiveStyle(
    { textAlign: 'center' as const, marginBottom: '2rem' },
    {},
    { marginBottom: '2.5rem' },
    { marginBottom: '3rem' }
  );

  const h1Style = getResponsiveStyle(
    { fontWeight: 700, color: colors.textPrimary, marginBottom: '1rem', lineHeight: 1.2 },
    { fontSize: '2rem' },
    { fontSize: '2.5rem' },
    { fontSize: '3rem' }
  );

  const summaryTextStyle = getResponsiveStyle(
    { color: colors.textSecondary, lineHeight: 1.6 },
    { fontSize: '1rem' },
    { fontSize: '1.125rem' },
    { fontSize: '1.25rem' }
  );

  const calculatorToolStyle = getResponsiveStyle(
    {
      background: 'linear-gradient(135deg, rgba(240, 249, 255, 0.9) 0%, rgba(224, 242, 254, 0.9) 100%)',
      borderRadius: '1rem',
      marginBottom: '2rem',
      boxShadow: '0 10px 40px rgba(0, 0, 0, 0.1)'
    },
    { padding: '1.5rem 1rem' },
    { padding: '2rem 1.5rem', borderRadius: '1.25rem' },
    { padding: '3rem 2rem', borderRadius: '1.5rem' }
  );

  const toolContainerStyle = getResponsiveStyle(
    { maxWidth: '100%', margin: '0 auto', background: 'white', borderRadius: '1rem', overflow: 'hidden' as const },
    {},
    { maxWidth: '700px' },
    { maxWidth: '900px' }
  );

  const toolHeaderStyle = getResponsiveStyle(
    {
      background: 'linear-gradient(135deg, #2563eb 0%, #7c3aed 100%)',
      color: 'white',
      textAlign: 'center' as const
    },
    { padding: '1.5rem' },
    { padding: '1.75rem' },
    { padding: '2rem' }
  );

  const toolHeaderTitleStyle = getResponsiveStyle(
    { margin: 0, fontWeight: 700, color: 'white' },
    { fontSize: '1.5rem' },
    { fontSize: '1.65rem' },
    { fontSize: '1.8rem' }
  );

  const formStyle = getResponsiveStyle(
    {},
    { padding: '1.5rem' },
    { padding: '1.75rem' },
    { padding: '2rem' }
  );

  const formGroupStyle = { marginBottom: '1.5rem' };

  const labelStyle = {
    display: 'block',
    marginBottom: '0.5rem',
    fontWeight: 600,
    color: colors.textPrimary,
    fontSize: '0.95rem'
  };

  const radioGroupStyle = getResponsiveStyle(
    { display: 'flex', gap: '1.5rem', marginTop: '0.5rem' },
    { flexDirection: 'column' as const, gap: '1rem' },
    { flexDirection: 'row' as const, gap: '2rem' },
    { flexDirection: 'row' as const, gap: '2rem' }
  );

  const radioOptionStyle = {
    display: 'flex',
    alignItems: 'center',
    cursor: 'pointer'
  };

  const radioInputStyle = {
    width: '1.2rem',
    height: '1.2rem',
    marginRight: '0.5rem',
    cursor: 'pointer',
    accentColor: '#2563eb'
  };

  const formRowStyle = getResponsiveStyle(
    { display: 'grid', gap: '1rem', marginBottom: '1.5rem' },
    { gridTemplateColumns: '1fr' },
    { gridTemplateColumns: '1fr 1fr' },
    { gridTemplateColumns: '1fr 1fr' }
  );

  const inputWrapperStyle = { position: 'relative' as const };

  const inputStyle = getResponsiveStyle(
    {
      width: '100%',
      border: '2px solid #e2e8f0',
      borderRadius: '0.5rem',
      transition: 'all 0.3s ease',
      boxSizing: 'border-box' as const,
      fontSize: '1rem'
    },
    { padding: '0.75rem 1rem' },
    { padding: '0.875rem 1.25rem' },
    { padding: '1rem 1.25rem' }
  );

  const inputUnitStyle = {
    position: 'absolute' as const,
    right: '1rem',
    top: '50%',
    transform: 'translateY(-50%)',
    color: '#64748b',
    fontSize: '0.9rem',
    pointerEvents: 'none' as const
  };

  const helperTextStyle = {
    fontSize: '0.85rem',
    color: '#64748b',
    marginTop: '0.25rem',
    fontStyle: 'italic' as const
  };

  const calculateButtonStyle = getResponsiveStyle(
    {
      width: '100%',
      background: 'linear-gradient(135deg, #2563eb 0%, #7c3aed 100%)',
      color: 'white',
      border: 'none',
      borderRadius: '0.5rem',
      fontWeight: 600,
      cursor: 'pointer',
      transition: 'all 0.3s ease',
      marginTop: '1rem'
    },
    { padding: '0.875rem 1.5rem', fontSize: '1rem' },
    { padding: '1rem 2rem', fontSize: '1.125rem' },
    { padding: '1.25rem 2.5rem', fontSize: '1.25rem' }
  );

  const errorMessageStyle = {
    background: colors.errorBg,
    color: '#991b1b',
    padding: '1rem',
    borderRadius: '0.5rem',
    marginTop: '1rem',
    fontSize: '0.95rem'
  };

  const resultsStyle = getResponsiveStyle(
    {
      background: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)',
      borderTop: '3px solid #e2e8f0'
    },
    { padding: '1.5rem' },
    { padding: '1.75rem' },
    { padding: '2rem' }
  );

  const resultsH3Style = getResponsiveStyle(
    { margin: '0 0 1.5rem 0', color: colors.textPrimary, textAlign: 'center' as const },
    { fontSize: '1.5rem' },
    { fontSize: '1.65rem' },
    { fontSize: '1.8rem' }
  );

  const resultMainStyle = getResponsiveStyle(
    {
      background: 'white',
      borderRadius: '0.75rem',
      textAlign: 'center' as const,
      marginBottom: '1.5rem',
      boxShadow: '0 4px 15px rgba(0, 0, 0, 0.08)'
    },
    { padding: '1.5rem' },
    { padding: '1.75rem' },
    { padding: '2rem' }
  );

  const resultMainLabelStyle = {
    fontSize: '1rem',
    color: '#64748b',
    marginBottom: '0.5rem',
    fontWeight: 500
  };

  const resultMainValueStyle = getResponsiveStyle(
    {
      fontWeight: 700,
      background: 'linear-gradient(135deg, #2563eb 0%, #7c3aed 100%)',
      WebkitBackgroundClip: 'text',
      WebkitTextFillColor: 'transparent',
      backgroundClip: 'text'
    },
    { fontSize: '2.5rem' },
    { fontSize: '2.75rem' },
    { fontSize: '3rem' }
  );

  const statusBadgeStyle = (isPassing: boolean) => ({
    display: 'inline-block',
    padding: '0.5rem 1.5rem',
    borderRadius: '2rem',
    fontWeight: 600,
    marginTop: '1rem',
    fontSize: '0.95rem',
    background: isPassing ? colors.successBg : colors.errorBg,
    color: isPassing ? '#166534' : '#991b1b'
  });

  const resultDetailsStyle = getResponsiveStyle(
    { display: 'grid', gap: '1rem', marginTop: '1.5rem' },
    { gridTemplateColumns: '1fr' },
    { gridTemplateColumns: 'repeat(2, 1fr)' },
    { gridTemplateColumns: 'repeat(3, 1fr)' }
  );

  const resultItemStyle = {
    background: 'white',
    padding: '1.25rem',
    borderRadius: '0.75rem',
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.05)'
  };

  const resultLabelStyle = {
    display: 'block',
    fontSize: '0.85rem',
    color: '#64748b',
    marginBottom: '0.5rem',
    fontWeight: 500
  };

  const resultValueStyle = {
    display: 'block',
    fontSize: '1.5rem',
    fontWeight: 700,
    color: colors.textPrimary
  };

  const standardsTableStyle = getResponsiveStyle(
    {
      background: 'white',
      borderRadius: '0.75rem',
      marginTop: '1.5rem',
      boxShadow: '0 2px 8px rgba(0, 0, 0, 0.05)',
      overflowX: 'auto' as const
    },
    { padding: '1rem' },
    { padding: '1.25rem' },
    { padding: '1.5rem' }
  );

  const standardsH4Style = {
    margin: '0 0 1rem 0',
    color: colors.textPrimary,
    fontSize: '1.1rem'
  };

  const tableStyle = {
    width: '100%',
    borderCollapse: 'collapse' as const
  };

  const thStyle = {
    padding: '0.75rem',
    textAlign: 'left' as const,
    borderBottom: '1px solid #e2e8f0',
    background: '#f8fafc',
    fontWeight: 600,
    color: '#475569',
    fontSize: '0.9rem'
  };

  const tdStyle = {
    padding: '0.75rem',
    textAlign: 'left' as const,
    borderBottom: '1px solid #e2e8f0',
    color: colors.textPrimary
  };

  const highlightRowStyle = {
    ...tdStyle,
    background: '#fef3c7'
  };

  const glassCardStyle = getResponsiveStyle(
    {
      background: colors.glassBg,
      backdropFilter: 'blur(20px) saturate(180%)',
      WebkitBackdropFilter: 'blur(20px) saturate(180%)',
      border: `1px solid ${colors.glassBorder}`,
      borderRadius: '1rem',
      boxShadow: '0 8px 32px 0 rgba(255, 90, 112, 0.08)',
      marginBottom: '2rem'
    },
    { padding: '1.5rem' },
    { padding: '2rem', borderRadius: '1.25rem' },
    { padding: '2.5rem', borderRadius: '1.5rem' }
  );

  const h2Style = getResponsiveStyle(
    { fontWeight: 600, color: colors.textPrimary, marginBottom: '1rem', marginTop: 0 },
    { fontSize: '1.5rem' },
    { fontSize: '1.75rem' },
    { fontSize: '2rem' }
  );

  const paragraphStyle = getResponsiveStyle(
    { lineHeight: 1.6, color: colors.textSecondary, marginBottom: '1rem' },
    { fontSize: '1rem' },
    { fontSize: '1.125rem' },
    { fontSize: '1.125rem' }
  );

  const listStyle = {
    ...paragraphStyle,
    paddingLeft: '1.5rem'
  };

  const listItemStyle = {
    marginBottom: '0.75rem'
  };

  const refLinkStyle = {
    color: colors.primary,
    textDecoration: 'none',
    fontWeight: 500,
    transition: 'color 0.3s ease'
  };

  return (
    <div style={containerStyle}>
      {/* Header */}
      <header style={headerStyle}>
        <h1 style={h1Style}>{t('title')}</h1>
        <p style={summaryTextStyle}>{t('subtitle')}</p>
      </header>

      {/* Calculator Tool */}
      <section style={calculatorToolStyle}>
        <div style={toolContainerStyle}>
          <div style={toolHeaderStyle}>
            <h2 style={toolHeaderTitleStyle}>🎖️ {t('toolTitle')}</h2>
          </div>

          <form style={formStyle} onSubmit={handleCalculate}>
            <div style={formGroupStyle}>
              <label style={labelStyle}>{t('genderLabel')}</label>
              <div style={radioGroupStyle}>
                <div style={radioOptionStyle}>
                  <input
                    type="radio"
                    id="gender-male"
                    name="gender"
                    value="male"
                    checked={inputs.gender === 'male'}
                    onChange={(e) => setInputs({...inputs, gender: e.target.value})}
                    style={radioInputStyle}
                  />
                  <label htmlFor="gender-male" style={{...labelStyle, margin: 0, cursor: 'pointer'}}>{t('male')}</label>
                </div>
                <div style={radioOptionStyle}>
                  <input
                    type="radio"
                    id="gender-female"
                    name="gender"
                    value="female"
                    checked={inputs.gender === 'female'}
                    onChange={(e) => setInputs({...inputs, gender: e.target.value})}
                    style={radioInputStyle}
                  />
                  <label htmlFor="gender-female" style={{...labelStyle, margin: 0, cursor: 'pointer'}}>{t('female')}</label>
                </div>
              </div>
            </div>

            <div style={formRowStyle}>
              <div style={formGroupStyle}>
                <label htmlFor="age" style={labelStyle}>{t('ageLabel')}</label>
                <input
                  type="number"
                  id="age"
                  min="17"
                  max="100"
                  step="1"
                  value={inputs.age}
                  onChange={(e) => setInputs({...inputs, age: parseInt(e.target.value)})}
                  style={inputStyle}
                  required
                />
              </div>

              <div style={formGroupStyle}>
                <label htmlFor="weight" style={labelStyle}>{t('weightLabel')}</label>
                <div style={inputWrapperStyle}>
                  <input
                    type="number"
                    id="weight"
                    min="50"
                    max="500"
                    step="0.5"
                    value={inputs.weight}
                    onChange={(e) => setInputs({...inputs, weight: parseFloat(e.target.value)})}
                    style={inputStyle}
                    required
                  />
                  <span style={inputUnitStyle}>{t('lbs')}</span>
                </div>
              </div>
            </div>

            <div style={formGroupStyle}>
              <label htmlFor="waist" style={labelStyle}>{t('waistLabel')}</label>
              <div style={inputWrapperStyle}>
                <input
                  type="number"
                  id="waist"
                  min="10"
                  max="100"
                  step="0.5"
                  value={inputs.waist}
                  onChange={(e) => setInputs({...inputs, waist: parseFloat(e.target.value)})}
                  style={inputStyle}
                  required
                />
                <span style={inputUnitStyle}>{t('inches')}</span>
              </div>
              <div style={helperTextStyle}>{t('waistHelper')}</div>
            </div>

            {errorMessage && (
              <div style={errorMessageStyle}>{errorMessage}</div>
            )}

            <button
              type="submit"
              style={calculateButtonStyle}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-2px)';
                e.currentTarget.style.boxShadow = '0 10px 25px rgba(37, 99, 235, 0.3)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = 'none';
              }}
            >
              {t('calculateButton')}
            </button>
          </form>

          {results && (
            <div style={resultsStyle} id="results-section">
              <h3 style={resultsH3Style}>📊 {t('resultsTitle')}</h3>

              <div style={resultMainStyle}>
                <div style={resultMainLabelStyle}>{t('bodyFatPercentage')}</div>
                <div style={resultMainValueStyle}>{results.bodyFat.toFixed(1)}%</div>
                <div style={statusBadgeStyle(results.isPassing)}>
                  {results.isPassing ? `✓ ${t('withinStandards')}` : `✗ ${t('exceedsStandards')}`}
                </div>
              </div>

              <div style={resultDetailsStyle}>
                <div style={resultItemStyle}>
                  <span style={resultLabelStyle}>{t('yourAgeGroup')}</span>
                  <span style={resultValueStyle}>{results.ageGroup}</span>
                </div>
                <div style={resultItemStyle}>
                  <span style={resultLabelStyle}>{t('maximumAllowed')}</span>
                  <span style={resultValueStyle}>{results.maxAllowed}%</span>
                </div>
                <div style={resultItemStyle}>
                  <span style={resultLabelStyle}>{t('status')}</span>
                  <span style={resultValueStyle}>{results.isPassing ? t('pass') : t('fail')}</span>
                </div>
              </div>

              <div style={standardsTableStyle}>
                <h4 style={standardsH4Style}>{t('standardsTableTitle')}</h4>
                <table style={tableStyle}>
                  <thead>
                    <tr>
                      <th style={thStyle}>{t('ageGroupColumn')}</th>
                      <th style={thStyle}>{t('maxBodyFatColumn')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {standards[inputs.gender as 'male' | 'female'].map((standard, index) => {
                      const isCurrentAge = inputs.age >= standard.min && inputs.age <= standard.max;
                      return (
                        <tr key={index}>
                          <td style={isCurrentAge ? highlightRowStyle : tdStyle}>{standard.ageRange}</td>
                          <td style={isCurrentAge ? highlightRowStyle : tdStyle}>{standard.maxBF}%</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      </section>

      {/* Intro Section */}
      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('introTitle')}</h2>
        <p style={paragraphStyle}>{t('introParagraph1')}</p>
        <p style={paragraphStyle}>{t('introParagraph2')}</p>
        <p style={paragraphStyle}>{t('introParagraph3')}</p>
      </section>

      {/* How to Use */}
      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('howToUseTitle')}</h2>
        <ol style={listStyle}>
          <li style={listItemStyle}>{t('howToStep1')}</li>
          <li style={listItemStyle}>{t('howToStep2')}</li>
          <li style={listItemStyle}>{t('howToStep3')}</li>
          <li style={listItemStyle}>{t('howToStep4')}</li>
          <li style={listItemStyle}>{t('howToStep5')}</li>
        </ol>
      </section>

      {/* Insights */}
      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('insightsTitle')}</h2>
        <p style={paragraphStyle}>{t('insightsParagraph1')}</p>
        <p style={paragraphStyle}>{t('insightsParagraph2')}</p>
        <p style={paragraphStyle}>{t('insightsParagraph3')}</p>
      </section>

      {/* Details */}
      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('detailsTitle')}</h2>
        <h3 style={{...h2Style, fontSize: screenSize === 'mobile' ? '1.25rem' : '1.5rem'}}>{t('detailsSubtitle1')}</h3>
        <p style={paragraphStyle}>{t('detailsParagraph1')}</p>

        <h3 style={{...h2Style, fontSize: screenSize === 'mobile' ? '1.25rem' : '1.5rem'}}>{t('detailsSubtitle2')}</h3>
        <p style={paragraphStyle}>{t('detailsParagraph2')}</p>

        <h3 style={{...h2Style, fontSize: screenSize === 'mobile' ? '1.25rem' : '1.5rem'}}>{t('detailsSubtitle3')}</h3>
        <ul style={listStyle}>
          <li style={listItemStyle}><strong>{t('maleStandards')}</strong>: {t('maleStandardsDesc')}</li>
          <li style={listItemStyle}><strong>{t('femaleStandards')}</strong>: {t('femaleStandardsDesc')}</li>
        </ul>
      </section>

      {/* FAQ */}
      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('faqTitle')}</h2>

        <h3 style={{...h2Style, fontSize: screenSize === 'mobile' ? '1.125rem' : '1.25rem'}}>{t('faq1Question')}</h3>
        <p style={paragraphStyle}>{t('faq1Answer')}</p>

        <h3 style={{...h2Style, fontSize: screenSize === 'mobile' ? '1.125rem' : '1.25rem'}}>{t('faq2Question')}</h3>
        <p style={paragraphStyle}>{t('faq2Answer')}</p>

        <h3 style={{...h2Style, fontSize: screenSize === 'mobile' ? '1.125rem' : '1.25rem'}}>{t('faq3Question')}</h3>
        <p style={paragraphStyle}>{t('faq3Answer')}</p>

        <h3 style={{...h2Style, fontSize: screenSize === 'mobile' ? '1.125rem' : '1.25rem'}}>{t('faq4Question')}</h3>
        <p style={paragraphStyle}>{t('faq4Answer')}</p>

        <h3 style={{...h2Style, fontSize: screenSize === 'mobile' ? '1.125rem' : '1.25rem'}}>{t('faq5Question')}</h3>
        <p style={paragraphStyle}>{t('faq5Answer')}</p>
      </section>

      {/* References */}
      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('referencesTitle')}</h2>
        <ul style={listStyle}>
          <li style={listItemStyle}>
            <a href="https://www.omnicalculator.com/health/army-body-fat" target="_blank" rel="noopener noreferrer" style={refLinkStyle}>
              {t('ref1')}
            </a>
          </li>
          <li style={listItemStyle}>
            <a href="https://www.gigacalculator.com/calculators/army-body-fat-calculator.php" target="_blank" rel="noopener noreferrer" style={refLinkStyle}>
              {t('ref2')}
            </a>
          </li>
          <li style={listItemStyle}>
            <a href="https://legionathletics.com/army-body-fat-calculator/" target="_blank" rel="noopener noreferrer" style={refLinkStyle}>
              {t('ref3')}
            </a>
          </li>
          <li style={listItemStyle}>
            <a href="https://www.armyresilience.army.mil/abcp/BodyFatCalculator.html" target="_blank" rel="noopener noreferrer" style={refLinkStyle}>
              {t('ref4')}
            </a>
          </li>
          <li style={listItemStyle}>
            <a href="https://www.calculator.net/army-body-fat-calculator.html" target="_blank" rel="noopener noreferrer" style={refLinkStyle}>
              {t('ref5')}
            </a>
          </li>
          <li style={listItemStyle}>
            <a href="https://www.armyresilience.army.mil/abcp/index.html" target="_blank" rel="noopener noreferrer" style={refLinkStyle}>
              {t('ref6')}
            </a>
          </li>
        </ul>
      </section>
    </div>
  );
}